import React from 'react';

import EnhancedButton from '@components/button/EnhancedButton';
import CollapsibleSection from '@components/common/CollapsibleSection';
import EnhancedModal from '@components/generic-modal/EnhancedModal';
import { downloadExcelFile } from '@modules/policy-management/excel/download-json-excel-service';
import { MAIN_THEME } from '@styles';


import { makeStyles } from 'tss-react/mui';
import { ErrorLogs } from '@common/types';

const useStyles = makeStyles()(() => ({
  errorResult: {
    height: '30px',
    backgroundColor: 'rgb(248, 206, 204)',
    paddingTop: 10,
    paddingLeft: 10,
    marginBottom: 10,
    color: 'rgb(253, 40, 56)',
    borderRadius: '8px'
  },
  paperContent: {
    paddingLeft: '20px !important',
    paddingTop: '0px !important',
    paddingBottom: '20px !important'
  }
}));

type Props = {
  title: string;
  errorReportFileName: string;
  errorLogs: ErrorLogs[];
  onClose: () => void;
  open: boolean;
};

const ErrorLogsPopup: React.FC<Props> = ({
  title,
  errorReportFileName,
  errorLogs,
  open,
  onClose
}) => {
  const nbOfErrors = errorLogs.length;
  const distinctRows = new Set(errorLogs.map((log) => log.row)).size;
  const errorResult = `Validation failed - ${nbOfErrors} errors found across ${distinctRows} rows`;
  const { classes } = useStyles();

  // Group errors by type
  const groupedErrors = errorLogs.reduce<
    Record<string, ErrorLogs[]>
  >((acc, log) => {
    if (!acc[log.type]) acc[log.type] = [];
    acc[log.type].push(log);

    return acc;
  }, {});
  const downloadExcel = () => {
    const headers = {
      type: { label: 'Type', visible: true, isDate: false, isCurrency: false },
      row: { label: 'Row', visible: true, isDate: false, isCurrency: false },
      column: {
        label: 'Column',
        visible: true,
        isDate: false,
        isCurrency: false
      },
      description: {
        label: 'Description',
        visible: true,
        isDate: false,
        isCurrency: false
      }
    };

    downloadExcelFile({
      data: errorLogs,
      filename: `${errorReportFileName}`,
      headers
    });
  };

  return (
    <EnhancedModal
      classes={{ paperContent: classes.paperContent }}
      open={open}
      blur={true}
      onClose={onClose}
      inlineTitle={title}
    >
      <div className={classes.errorResult}>{errorResult}</div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '15px'
        }}
      >
        {Object.entries(groupedErrors).map(([type, logs], index) => (
          <CollapsibleSection
            key={index}
            title={type}
            open={false}
            count={logs.length} // optional: default open
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                fontFamily: 'Roboto, Helvetica, Arial, sans-serif'
              }}
            >
              {logs.map((log, i) => (
                <div
                  key={i}
                  style={{
                    color: 'rgb(161 168 168)',
                    fontSize: MAIN_THEME.fontSize.sm
                  }}
                >
                  {`Row ${log.row}, Column '${log.column}'`}
                </div>
              ))}
            </div>
          </CollapsibleSection>
        ))}
        <div
          style={{
            display: 'flex',
            gap: 20,
            flexDirection: 'row-reverse',
            marginTop: 20
          }}
        >
          <EnhancedButton isPrimary onClick={onClose}>
            Try Again
          </EnhancedButton>
          <EnhancedButton
            style={{ border: '2px solid' }}
            onClick={() => {
              downloadExcel();
            }}
          >
            Download Error Report
          </EnhancedButton>
        </div>
      </div>
    </EnhancedModal>
  );
};

export { ErrorLogsPopup };
